/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { motion } from "framer-motion";
import { Check, Eye, EyeOff, X } from "lucide-react";
import type { ReactElement } from "react";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { LoadingDots } from "../../shared/components/Loading";
import { Button } from "../../shared/components/ui/button";
import { Checkbox } from "../../shared/components/ui/checkbox";
import { Input } from "../../shared/components/ui/input";
import { Label } from "../../shared/components/ui/label";
import { useToast } from "../../shared/hooks/use-toast";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

/**
 * Password validation rules
 */
type PasswordRule = {
  readonly test: (password: string) => boolean;
  readonly label: string;
};

const PASSWORD_RULES: readonly PasswordRule[] = [
  { test: (p) => p.length >= 8, label: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p) => /\d/.test(p), label: "One number" },
] as const;

/**
 * Calculate password strength (0-3)
 */
function getPasswordStrength(password: string): number {
  return PASSWORD_RULES.filter((rule) => rule.test(password)).length;
}

/**
 * Get password strength label and color
 */
function getPasswordStrengthDisplay(strength: number): {
  label: string;
  color: string;
} {
  if (strength === 0) return { label: "Weak", color: "text-neutral-600" };
  if (strength === 1) return { label: "Weak", color: "text-red-400" };
  if (strength === 2) return { label: "Fair", color: "text-yellow-400" };
  return { label: "Strong", color: "text-green-400" };
}

/**
 * Get password strength indicator color based on level
 */
function getPasswordLevelColor(level: number, passwordStrength: number): string {
  if (level > passwordStrength) return "bg-neutral-800";
  if (level === 1) return "bg-red-400";
  if (level === 2) return "bg-yellow-400";
  return "bg-green-400";
}

/**
 * Validate email format
 * Uses a ReDoS-safe regex pattern with bounded quantifiers to prevent catastrophic backtracking.
 * Pattern allows standard email formats while rejecting malicious inputs.
 */
function isValidEmail(email: string): boolean {
  // ReDoS-safe email validation - based on OWASP recommendations
  // Limits length and uses possessive-like matching to prevent backtracking issues
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Additional length check to prevent DoS via extremely long inputs
  if (email.length > 254) return false;

  return emailRegex.test(email);
}

export function SignUpPage(): ReactElement {
  const { signUp, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPasswordMismatch, setShowPasswordMismatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { isTransitioning, transitionTo } = useTransitionNavigation({
    onTransitionStart: () => console.log("🎬 Starting SignUp → Onboarding transition"),
    onNavigationComplete: () => console.log("✅ Navigated to Onboarding"),
  });

  const passwordStrength = getPasswordStrength(password);
  const strengthDisplay = getPasswordStrengthDisplay(passwordStrength);
  const isPasswordValid = passwordStrength === PASSWORD_RULES.length;
  const isEmailValid = email ? isValidEmail(email) : true; // Don't show error on empty
  const passwordsMatch = confirmPassword ? password === confirmPassword : true; // Don't show error on empty

  // Stable navigation handler (prevent event handler orphaning during View Transitions)
  const handleNavigateSignIn = useCallback(() => {
    transitionTo("/sign-in", pageTransitions.slideHorizontal);
  }, [transitionTo]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Manually validate required fields to show custom toast instead of browser validation
    if (!email.trim()) {
      toast({
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    if (!password?.trim()) {
      toast({
        description: "Please enter a password",
        variant: "destructive",
      });
      return;
    }
    if (!confirmPassword?.trim()) {
      toast({
        description: "Please confirm your password",
        variant: "destructive",
      });
      return;
    }
    void performSignUp();
  };

  const performSignUp = async (): Promise<void> => {
    // Validation (already checked in handleSubmit, but double-check)
    if (!isValidEmail(email.trim())) {
      toast({
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!isPasswordValid) {
      toast({
        description: "Password does not meet requirements",
        variant: "destructive",
      });
      return;
    }

    if (!acceptedTos) {
      toast({
        description: "You must accept the Terms of Service and Privacy Policy",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      await signUp({ email: email.trim(), password });
      // After successful sign-up, redirect to onboarding
      transitionTo("/user/welcome", pageTransitions.slideHorizontal);
    } catch (err) {
      console.error("Sign up failed:", err);

      // Map API error codes to user-friendly messages
      let errorMessage = "Unable to create account. Please try again.";

      if (err instanceof Error) {
        const message = err.message.toLowerCase();
        if (message.includes("already exists") || message.includes("duplicate")) {
          errorMessage = "An account with this email already exists.";
        } else if (message.includes("429") || message.includes("rate limit") || message.includes("too many")) {
          errorMessage = "Too many attempts. Please wait a moment and try again.";
        } else if (message.includes("network") || message.includes("fetch")) {
          errorMessage = "Network error. Please check your connection.";
        } else if (message.includes("500")) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      toast({
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || authLoading;

  return (
    <motion.div
      className="relative min-h-screen flex items-center justify-center"
      initial="initial"
      animate={isTransitioning ? "exit" : "animate"}
      exit="exit"
      variants={pageTransitions.scaleDown}
    >
      {/* Draggable titlebar region for Electron (macOS only) */}
      <div
        className="absolute top-0 left-0 right-0 h-[52px] z-50 electron-drag-region"
        style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
      />

      {/* Content layer with card */}
      <div className={`relative z-10 mx-auto w-[400px]`}>
        {/* Card background */}
        <div className="absolute inset-0 bg-black/80 rounded-lg border border-neutral-800/60" />

        {/* Card content */}
        <div className="relative p-6">
          <h1 className="text-xl font-semibold mb-2 text-white text-center">Create your Lumi account</h1>
          <p className="text-sm text-lumi-accent text-center mb-6">Start your 30-day free trial</p>

          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setShowEmailError(false);
                }}
                onBlur={() => setShowEmailError(true)}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
                className={showEmailError && !isEmailValid ? "border-red-400" : ""}
              />
              {showEmailError && !isEmailValid && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex items-center gap-2 text-xs text-red-400 overflow-hidden"
                >
                  <X size={12} />
                  <span>Please enter a valid email address</span>
                </motion.div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setShowPasswordRules(true)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-200 transition-colors"
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Password requirements and strength indicator */}
              {showPasswordRules && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="space-y-2 text-xs overflow-hidden"
                >
                  {/* Password strength indicator */}
                  {password && (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3].map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full transition-colors ${getPasswordLevelColor(level, passwordStrength)}`}
                          />
                        ))}
                      </div>
                      {strengthDisplay.label && <span className={strengthDisplay.color}>{strengthDisplay.label}</span>}
                    </div>
                  )}

                  {/* Password requirements list */}
                  <div className="space-y-1">
                    {PASSWORD_RULES.map((rule) => {
                      const isValid = rule.test(password);
                      return (
                        <div
                          key={rule.label}
                          className={`flex items-center gap-2 ${isValid ? "text-green-400" : "text-neutral-500"}`}
                        >
                          {isValid ? <Check size={12} /> : <X size={12} />}
                          <span>{rule.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setShowPasswordMismatch(false);
                  }}
                  onBlur={() => setShowPasswordMismatch(true)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  className={`pr-10 ${showPasswordMismatch && !passwordsMatch ? "border-red-400" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-200 transition-colors"
                  disabled={isLoading}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {showPasswordMismatch && !passwordsMatch && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex items-center gap-2 text-xs text-red-400 overflow-hidden"
                >
                  <X size={12} />
                  <span>Passwords do not match</span>
                </motion.div>
              )}
              {showPasswordMismatch && passwordsMatch && confirmPassword && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex items-center gap-2 text-xs text-green-400 overflow-hidden"
                >
                  <Check size={12} />
                  <span>Passwords match</span>
                </motion.div>
              )}
            </div>

            {/* Terms of Service Checkbox */}
            <div className="flex items-center gap-3 pt-2">
              <Checkbox
                id="acceptTos"
                checked={acceptedTos}
                onCheckedChange={(checked) => setAcceptedTos(checked === true)}
                disabled={isLoading}
              />
              <label htmlFor="acceptTos" className="text-xs text-neutral-300 flex-1 cursor-pointer">
                I agree to the{" "}
                <Link
                  to="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lumi-accent hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lumi-accent hover:underline"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            <Button type="submit" disabled={isLoading || !acceptedTos} className="mt-2" variant="outline">
              {isLoading ? (
                <span className="flex items-center">
                  Creating Account
                  <LoadingDots className="ml-1" />
                </span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-2 text-center">
            <Button
              type="button"
              variant="ghost-sm"
              onClick={handleNavigateSignIn}
              disabled={isLoading}
              className="text-xs text-lumi-accent hover:text-lumi-accent-80 underline"
            >
              Already have an account? Sign in
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
